import React from 'react';
import { Code2 } from 'lucide-react';
import type { RepoStackAnalysis } from '../types';

interface LanguageFilterSidebarProps {
  data: RepoStackAnalysis;
  selectedLanguageIds: Set<string>;
  onToggleLanguage: (id: string) => void;
  onClear: () => void;
}

export const LanguageFilterSidebar: React.FC<LanguageFilterSidebarProps> = ({
  data,
  selectedLanguageIds,
  onToggleLanguage,
  onClear,
}) => {
  const nameById: Record<string, string> = {};
  for (const n of data.graph.nodes) nameById[n.id] = n.name;

  const languages = [...data.repository.languages].sort((a, b) => {
    if (a.primary && !b.primary) return -1;
    if (!a.primary && b.primary) return 1;
    return b.confidence - a.confidence;
  });

  if (languages.length === 0) return null;

  const hasSelection = selectedLanguageIds.size > 0;

  return (
    <aside className="absolute left-0 top-0 bottom-0 z-10 w-56 border-r border-slate-200 dark:border-slate-700/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm overflow-y-auto">
      <div className="px-4 pt-4 pb-3 border-b border-slate-200 dark:border-slate-700/50">
        <div className="flex items-center gap-2 mb-1">
          <Code2 className="w-4 h-4 text-teal-500" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-300">
            Languages
          </h3>
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
          Filter the map by detected languages
        </p>
      </div>

      <ul className="px-2 py-2 space-y-0.5">
        {languages.map((lang) => {
          const selected = selectedLanguageIds.has(lang.id);
          const name = nameById[lang.id] ?? lang.id;
          const confPct = Math.round(lang.confidence * 100);
          return (
            <li key={lang.id}>
              <button
                onClick={() => onToggleLanguage(lang.id)}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-left text-xs transition-colors ${
                  selected
                    ? 'bg-teal-500/10 text-teal-700 dark:text-teal-300'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                }`}
                aria-pressed={selected}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      selected ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  />
                  <span className="truncate font-medium">{name}</span>
                  {lang.primary && (
                    <span className="text-[9px] uppercase tracking-wide text-slate-400 dark:text-slate-500 shrink-0">
                      primary
                    </span>
                  )}
                </span>
                <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0 tabular-nums">
                  {confPct}%
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {hasSelection && (
        <div className="px-3 pb-3">
          <button
            onClick={onClear}
            className="w-full text-[11px] text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 underline-offset-2 hover:underline transition-colors"
          >
            Clear filter ({selectedLanguageIds.size} selected)
          </button>
        </div>
      )}
    </aside>
  );
};
